import { DateTime } from "luxon";
import type { ScheduleChangesWindowPreset } from "../components/scheduleChanges/types";

/**
 * "Since my last visit" for the schedule-changes surfaces, per signed-in user and department — so on a shared
 * workstation one admin's visit never moves another's window.
 *
 * - The FIRST load of a browser session (no session marker yet) opens a window that starts at the stored last visit,
 *   but never later than 2 days ago — someone who looked an hour ago still sees the last two days — and never earlier
 *   than 13 days ago (a day inside the server's 14-day limit). With nothing stored (first visit on this device) it is the last 2 days.
 * - That window start is kept in `sessionStorage`, so refreshing the page keeps the same window for the whole session.
 * - The current time is then written as the new last visit, which the NEXT session starts from.
 *
 * Storage is best effort: a private window, a full quota or a corrupt value never breaks the page. Every failure is
 * returned as a `LastVisitStorageError` (never thrown, never swallowed) so the hook can report it, and the window falls
 * back to what the readable data supports.
 *
 * Split into a pure `resolve` (safe to run during render) and a `commit` that writes (run it in an effect).
 */

/** The shortest look-back a session opens with. */
export const LAST_VISIT_MIN_LOOKBACK = { hours: 48 } as const;
/**
 * The longest: 13 days, the window a request asks for at most (`SERVER_MAX_WINDOW` — a day inside the server's 14-day
 * limit, which a 14-day local span would cross at a DST fall-back). A visit two months ago opens the last 13 days, so
 * the "since …" caption names the window the data actually covers.
 */
export const LAST_VISIT_MAX_LOOKBACK = { days: 13 } as const;
export const LAST_2_DAYS_LOOKBACK = { days: 2 } as const;
export const LAST_WEEK_LOOKBACK = { weeks: 1 } as const;

const LAST_VISIT_KEY_PREFIX = "scheduleChanges.lastVisit.";
const SESSION_WINDOW_KEY_PREFIX = "scheduleChanges.sessionWindow.";

/** Who and where a stored visit belongs to. */
export type LastVisitOwner = { userId: string; departmentId: string };

const ownerSuffix = ({ userId, departmentId }: LastVisitOwner): string => `${userId}.${departmentId}`;

export const lastVisitStorageKey = (owner: LastVisitOwner): string => `${LAST_VISIT_KEY_PREFIX}${ownerSuffix(owner)}`;
export const sessionWindowStorageKey = (owner: LastVisitOwner): string => `${SESSION_WINDOW_KEY_PREFIX}${ownerSuffix(owner)}`;

/** The two `Storage` methods this module uses — so a test can hand in a store that throws. */
export type StorageLike = Pick<Storage, "getItem" | "setItem">;

export type LastVisitStorageOperation = "read_last_visit" | "write_last_visit" | "read_session_window" | "write_session_window";

export type LastVisitStorageError = { operation: LastVisitStorageOperation; error: unknown };

export type LastVisitWindowInput = LastVisitOwner & {
	timezone: string;
	/** The current time, in the department's zone. */
	now: DateTime;
	local: StorageLike;
	session: StorageLike;
};

export type ResolvedLastVisitWindow = {
	/** Where "since my last visit" starts. */
	since: DateTime;
	/** The last visit this session started from, or null on the first visit from this device. */
	lastVisit: DateTime | null;
	/** The time the window was resolved at — the presets count back from it. */
	now: DateTime;
	/** True when this load started the session: `commitLastVisitWindow` must write the window and the new last visit. */
	isSessionStart: boolean;
	storageErrors: LastVisitStorageError[];
};

type SessionWindowRecord = { since: string; lastVisit: string | null };

const readItem = (storage: StorageLike, key: string, operation: LastVisitStorageOperation, errors: LastVisitStorageError[]): string | null => {
	try {
		return storage.getItem(key);
	} catch (error) {
		errors.push({ operation, error });
		return null;
	}
};

const writeItem = (storage: StorageLike, key: string, value: string, operation: LastVisitStorageOperation, errors: LastVisitStorageError[]): void => {
	try {
		storage.setItem(key, value);
	} catch (error) {
		errors.push({ operation, error });
	}
};

/** An ISO instant in the department's zone, or null (with an error recorded) when it can't be read. */
const parseInstant = (iso: string, timezone: string, operation: LastVisitStorageOperation, errors: LastVisitStorageError[]): DateTime | null => {
	const parsed = DateTime.fromISO(iso, { setZone: true }).setZone(timezone);
	if (parsed.isValid) return parsed;
	errors.push({ operation, error: new Error(`unreadable stored instant "${iso}"`) });
	return null;
};

const isSessionWindowRecord = (value: unknown): value is SessionWindowRecord => {
	if (typeof value !== "object" || value === null) return false;
	const record = value as Record<string, unknown>; // narrowed from `object` to read its fields; each is checked below
	return typeof record.since === "string" && (record.lastVisit === null || typeof record.lastVisit === "string");
};

/** The window this session already opened, or null when there is none (or it can't be read). */
const readSessionWindow = (input: LastVisitWindowInput, errors: LastVisitStorageError[]): { since: DateTime; lastVisit: DateTime | null } | null => {
	const raw = readItem(input.session, sessionWindowStorageKey(input), "read_session_window", errors);
	if (raw === null) return null;
	let record: unknown;
	try {
		record = JSON.parse(raw);
	} catch (error) {
		errors.push({ operation: "read_session_window", error });
		return null;
	}
	if (!isSessionWindowRecord(record)) {
		errors.push({ operation: "read_session_window", error: new Error("stored session window has the wrong shape") });
		return null;
	}
	const since = parseInstant(record.since, input.timezone, "read_session_window", errors);
	if (since === null) return null;
	const lastVisit = record.lastVisit === null ? null : parseInstant(record.lastVisit, input.timezone, "read_session_window", errors);
	return { since, lastVisit };
};

const readLastVisit = (input: LastVisitWindowInput, errors: LastVisitStorageError[]): DateTime | null => {
	const raw = readItem(input.local, lastVisitStorageKey(input), "read_last_visit", errors);
	return raw === null ? null : parseInstant(raw, input.timezone, "read_last_visit", errors);
};

/** Pure: reads storage, never writes. */
export const resolveLastVisitWindow = (input: LastVisitWindowInput): ResolvedLastVisitWindow => {
	const storageErrors: LastVisitStorageError[] = [];
	const sessionWindow = readSessionWindow(input, storageErrors);
	if (sessionWindow !== null) {
		return { ...sessionWindow, now: input.now, isSessionStart: false, storageErrors };
	}

	const lastVisit = readLastVisit(input, storageErrors);
	const floor = input.now.minus(LAST_VISIT_MIN_LOOKBACK);
	const ceiling = input.now.minus(LAST_VISIT_MAX_LOOKBACK);
	// DEFAULT: no last visit on this device (or it can't be read) opens the minimum window, the last 2 days — the
	// same window a recent visitor gets, so a first visit still shows what changed yesterday and today.
	const since = lastVisit === null ? floor : DateTime.max(DateTime.min(lastVisit, floor), ceiling);
	return { since, lastVisit, now: input.now, isSessionStart: true, storageErrors };
};

/** Writes the session's window and the new last visit. A no-op for a load that didn't start the session. */
export const commitLastVisitWindow = (resolved: ResolvedLastVisitWindow, input: LastVisitWindowInput): LastVisitStorageError[] => {
	const errors: LastVisitStorageError[] = [];
	if (!resolved.isSessionStart) return errors;

	const sinceIso = resolved.since.toISO();
	const nowIso = resolved.now.toISO();
	if (sinceIso === null || nowIso === null) {
		errors.push({ operation: "write_session_window", error: new Error("window has an invalid instant") });
		return errors;
	}
	const record: SessionWindowRecord = { since: sinceIso, lastVisit: resolved.lastVisit === null ? null : resolved.lastVisit.toISO() };
	writeItem(input.session, sessionWindowStorageKey(input), JSON.stringify(record), "write_session_window", errors);
	writeItem(input.local, lastVisitStorageKey(input), nowIso, "write_last_visit", errors);
	return errors;
};

/** The windows a scheduler can switch between, in the order a picker lists them. */
export const buildWindowPresets = (resolved: Pick<ResolvedLastVisitWindow, "since" | "now">): ScheduleChangesWindowPreset[] => [
	{ id: "sinceLastVisit", since: resolved.since },
	{ id: "last2Days", since: resolved.now.minus(LAST_2_DAYS_LOOKBACK) },
	{ id: "lastWeek", since: resolved.now.minus(LAST_WEEK_LOOKBACK) },
];
